import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Autocomplete, Box, Chip, Link, TextField, TextFieldProps } from '@mui/material';
import type { CodeKind, CodeNames, CodeOption, InquiryFilters } from '../../types/inquiry';

/**
 * Shared pieces of the PR Inquiry and PO Inquiry pages: the filters kept in the URL (so Back from a PR or a PO
 * returns to the same search), a text box that applies after a pause, multi-select pickers, document links and
 * amount formatting.
 */

/**
 * The page's filters, read from and written to the URL. Changing a filter goes back to page 1. With `storageKey` the
 * search is also remembered for the browser session and restored when the page opens without one (a tab switch
 * opens the page's bare path).
 */
export function useUrlFilters(keys: readonly string[], storageKey?: string) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!storageKey || keys.some((k) => params.get(k))) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) setParams(new URLSearchParams(saved), { replace: true });
    } catch {
      /* storage unavailable: start empty */
    }
    // Only when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!storageKey) return;
    const kept = new URLSearchParams();
    for (const k of keys) {
      const v = params.get(k);
      if (v) kept.set(k, v);
    }
    try { sessionStorage.setItem(storageKey, kept.toString()); } catch { /* not kept */ }
  }, [params, keys, storageKey]);
  const filters: InquiryFilters = useMemo(() => {
    const f: InquiryFilters = {};
    for (const k of keys) {
      const v = params.get(k);
      if (v) f[k] = v;
    }
    return f;
  }, [params, keys]);
  const update = useCallback((patch: Record<string, string | undefined>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === '') next.delete(k);
        else next.set(k, v);
      }
      if (!('page' in patch)) next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);
  const clear = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams();
      // Keep the sort and the page size.
      for (const k of ['sort', 'dir', 'pageSize']) {
        const v = prev.get(k);
        if (v) next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return { filters, update, clear };
}

/** 'a,b' <-> ['a', 'b']. */
export const splitList = (v: string | undefined): string[] => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
export const joinList = (v: Array<string | number>): string => v.map(String).join(',');

/** A text box that applies its value 400 ms after the last keystroke (and at once on Enter). */
export const DelayedTextField: React.FC<Omit<TextFieldProps, 'value' | 'onChange'> & { value: string; onValue: (v: string) => void }> = ({ value, onValue, ...rest }) => {
  const [text, setText] = useState(value);
  const applied = useRef(value);
  useEffect(() => { setText(value); applied.current = value; }, [value]);
  useEffect(() => {
    if (text === applied.current) return undefined;
    const t = setTimeout(() => { applied.current = text; onValue(text.trim()); }, 400);
    return () => clearTimeout(t);
  }, [text, onValue]);
  return (
    <TextField
      {...rest}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { applied.current = text; onValue(text.trim()); } }}
    />
  );
};

export interface PickOption { value: string; label: string; hint?: string }

/** Several choices of a list, shown as chips. */
export const MultiPicker: React.FC<{
  id: string;
  label: string;
  options: PickOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}> = ({ id, label, options, value, onChange, placeholder }) => {
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  // A value from the URL that the list does not offer is kept and shown as it is.
  const selected = value.map((v) => byValue.get(v) || { value: v, label: v });
  return (
    <Autocomplete
      id={id}
      multiple
      size="small"
      limitTags={2}
      options={options}
      value={selected}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionLabel={(o) => o.label}
      onChange={(_e, v) => onChange(v.map((o) => o.value))}
      renderOption={(props, o) => (
        <li {...props} key={o.value}>
          <Box>
            {o.label}
            {o.hint ? <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>{o.hint}</Box> : null}
          </Box>
        </li>
      )}
      renderTags={(tags, getTagProps) => tags.map((o, i) => <Chip {...getTagProps({ index: i })} key={o.value} size="small" label={o.label.length > 24 ? `${o.label.slice(0, 22)}…` : o.label} />)}
      renderInput={(params) => <TextField {...params} label={label} placeholder={value.length ? undefined : placeholder} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
    />
  );
};

/** Code pickers' options: 'CODE Name'. */
export const codeOptions = (codes: CodeOption[]): PickOption[] =>
  codes.map((c) => ({ value: c.code, label: c.name ? `${c.code} ${c.name}` : c.code }));

/** Codes with their names, for a table cell. */
export function codesText(codes: string[], kind: CodeKind, names: CodeNames): string {
  return codes.map((c) => (names[kind]?.[c] ? `${c} ${names[kind][c]}` : c)).join(', ');
}

/** Links to documents, the first `max` shown and the rest behind "+n more". Clicks never reach the row. */
export function DocLinks<T>({ items, label, onOpen, max = 3, ariaLabel }: {
  items: T[];
  label: (item: T) => string;
  onOpen?: (item: T) => void;
  max?: number;
  ariaLabel: (item: T) => string;
}): React.ReactElement | null {
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, max);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
      {shown.map((item, i) => (onOpen ? (
        <Link
          key={i}
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          aria-label={ariaLabel(item)}
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onOpen(item); }}
          onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {label(item)}
        </Link>
      ) : (
        <Box key={i} component="span" sx={{ whiteSpace: 'nowrap', fontSize: 14 }}>{label(item)}</Box>
      )))}
      {items.length > max && (
        <Link
          component="button"
          type="button"
          variant="body2"
          color="text.secondary"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAll(!all); }}
          onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
        >
          {all ? 'less' : `+${items.length - max} more`}
        </Link>
      )}
    </Box>
  );
}

/** An amount with the company's decimals (2 for another currency). */
export function money(amount: number | null | undefined, currency: string, companyCurrency: string, companyDecimals: number): string {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return '';
  const d = !currency || currency === companyCurrency ? companyDecimals : 2;
  const text = Number(amount).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
  return currency ? `${text} ${currency}` : text;
}

export const quantity = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

export type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

/** A colour per status, PRPO's and SAP's. */
export function statusColor(status: string | null | undefined): ChipColor {
  const s = String(status || '');
  if (s === 'Rejected' || s === 'Cancelled') return 'error';
  if (s.startsWith('Pending')) return 'warning';
  if (s === 'Approved' || s === 'Open') return 'info';
  if (s.startsWith('PostedToSAP') || s === 'ConvertedToPO') return 'success';
  return 'default';
}

/** Whether the signed-in user may open a page (its module, or Admin). */
export const hasModule = (user: { role?: string; allowedmodules?: string[] } | null | undefined, key: string): boolean =>
  !!user && (user.role === 'Admin' || (Array.isArray(user.allowedmodules) && user.allowedmodules.includes(key)));
